import type {
  CallOffHistoryQueryParams,
  CallOffHistoryResponse,
} from './types';

import {
  type QueryFunctionContext,
  queryOptions,
  skipToken,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { type UUID } from '~/types/common';

import { queryKeysCallOff } from '../queryKeys';

import { fetchCallOffHistory } from './fetchCallOffHistory';

/**
 * Generates the query options for the call-off history query.
 *
 * @param callOffId - The ID of the call-off.
 * @param queryParams - Pagination parameters.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getCallOffHistoryQueryOptions = <TData = CallOffHistoryResponse>({
  callOffId,
  queryParams,
  options,
}: {
  callOffId: UUID | undefined;
  queryParams?: CallOffHistoryQueryParams;
  options?: Omit<
    UseQueryOptions<CallOffHistoryResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    queryFn: callOffId
      ? async ({ signal }: QueryFunctionContext) =>
          fetchCallOffHistory(callOffId, queryParams, { signal })
      : skipToken,
    queryKey: queryKeysCallOff.getHistory(callOffId ?? ''),
    staleTime: ms('60s'),
    ...options,
  });

/**
 * React Query based custom hook for getting the version history of a call-off.
 *
 * @param callOffId - The ID of the call-off.
 * @param queryParams - Pagination parameters.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 * @see https://app.dev.vestigas.com/redoc#tag/Call-Offs/operation/get_history_calloffs__calloff_id__history_get
 */
export const useQueryCallOffHistory = (
  callOffId: UUID | undefined,
  queryParams?: CallOffHistoryQueryParams,
  options?: Omit<
    UseQueryOptions<CallOffHistoryResponse, Error, CallOffHistoryResponse>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery(
    getCallOffHistoryQueryOptions<CallOffHistoryResponse>({
      callOffId,
      queryParams,
      options,
    }),
  );
